import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { Reservation, TimeSlot } from '../../models/types';
import { getMarinaSettings } from './marinaSettingsService';
import { getVesselPricingOptions } from './pricingService';
import { updateReservation } from './reservationService';
import { trackReservationUpdate } from './reservationHistoryService';
import { format, addHours, parseISO, isWithinInterval } from 'date-fns';

// Interface for extension options
export interface ExtensionOption {
  hours: number;
  endTime: string;
  newEndTime: string;
  additionalCost: number;
  totalNewCost: number;
  isAvailable: boolean;
  conflictReason?: string;
}

// Interface for extension request
export interface ExtensionRequest {
  reservationId: string;
  additionalHours: number;
  extendedBy: string;
  channel: 'staff' | 'customer';
  notes?: string;
}

// Interface for extension result
export interface ExtensionResult {
  success: boolean;
  newEndTime: string;
  additionalCost: number;
  newTotalCost: number;
  message: string;
  error?: string;
}

// Helper function to convert time string to minutes
const timeToMinutes = (timeStr: string): number => {
  if (!timeStr || typeof timeStr !== 'string') {
    console.error('Invalid time string passed to timeToMinutes:', timeStr);
    return 0;
  }
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    console.error('Invalid time format in timeToMinutes:', timeStr);
    return 0;
  }
  
  return hours * 60 + minutes;
};

// Helper function to convert minutes to time string
const minutesToTime = (minutes: number): string => {
  if (isNaN(minutes) || minutes < 0) {
    console.error('Invalid minutes passed to minutesToTime:', minutes);
    return '12:00 AM';
  }
  
  // Handle minutes that exceed 24 hours (wrap around)
  const normalizedMinutes = minutes % (24 * 60);
  const hours24 = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const displayHours = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
};

// Helper function to convert minutes to 24-hour format
const minutesToTime24 = (minutes: number): string => {
  if (isNaN(minutes) || minutes < 0) {
    console.error('Invalid minutes passed to minutesToTime24:', minutes);
    return '00:00';
  }
  
  // Handle minutes that exceed 24 hours (wrap around)
  const normalizedMinutes = minutes % (24 * 60);
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Helper function to convert 12-hour time to 24-hour format
const convertTo24Hour = (timeStr: string): string => {
  if (!timeStr || typeof timeStr !== 'string') {
    console.error('Invalid time string passed to convertTo24Hour:', timeStr);
    return '00:00';
  }
  
  // Handle cases where timeStr might already be in 24-hour format
  if (!timeStr.includes(' ')) {
    // Already in 24-hour format (e.g., "14:30")
    return timeStr;
  }
  
  const parts = timeStr.split(' ');
  if (parts.length !== 2) {
    console.error('Invalid time format in convertTo24Hour:', timeStr);
    return '00:00';
  }
  
  const [time, period] = parts;
  const timeParts = time.split(':');
  if (timeParts.length !== 2) {
    console.error('Invalid time format in convertTo24Hour:', timeStr);
    return '00:00';
  }
  
  const [hours, minutes] = timeParts.map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    console.error('Invalid time numbers in convertTo24Hour:', timeStr);
    return '00:00';
  }
  
  let hour24 = hours;
  if (period === 'PM' && hours !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hours === 12) {
    hour24 = 0;
  }
  
  return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Check if vessel is available for extension
export const checkExtensionAvailability = async (
  vesselId: string,
  date: string,
  currentEndTime: string,
  additionalHours: number
): Promise<{ available: boolean; conflictReason?: string }> => {
  try {
    console.log('Checking extension availability:', { vesselId, date, currentEndTime, additionalHours });
    
    // Validate inputs
    if (!vesselId || !date || !currentEndTime) {
      console.error('Missing required parameters for extension availability check');
      return {
        available: false,
        conflictReason: 'Missing required parameters'
      };
    }
    
    if (additionalHours <= 0) {
      console.error('Invalid additional hours:', additionalHours);
      return {
        available: false,
        conflictReason: 'Invalid extension duration'
      };
    }
    
    // Convert current end time to 24-hour format and calculate new end time
    const currentEnd24 = convertTo24Hour(currentEndTime);
    const currentEndMinutes = timeToMinutes(currentEnd24);
    
    // Check if time conversion failed
    if (currentEnd24 === '00:00' && currentEndTime !== '12:00 AM') {
      console.error('Time conversion failed for:', currentEndTime);
      return {
        available: false,
        conflictReason: 'Invalid current end time format'
      };
    }
    
    const newEndMinutes = currentEndMinutes + (additionalHours * 60);
    const newEnd24 = minutesToTime24(newEndMinutes);
    
    // Create the extended time slot for checking
    const extendedTimeSlot = `${currentEnd24}-${newEnd24}` as TimeSlot;
    
    console.log('Extended time slot to check:', extendedTimeSlot);
    
    // Query for existing reservations on the same vessel and date
    const reservationsRef = collection(db, 'reservations');
    const q = query(
      reservationsRef,
      where('vesselId', '==', vesselId),
      where('date', '==', date),
      where('status', 'in', ['pending', 'confirmed'])
    );
    
    const querySnapshot = await getDocs(q);
    
    // Check for conflicts with existing reservations
    for (const docSnapshot of querySnapshot.docs) {
      const reservation = docSnapshot.data() as Reservation;
      
      // Skip reservations with malformed or missing time slots
      if (!reservation.timeSlot || !reservation.timeSlot.includes('-')) {
        console.warn('Reservation has malformed timeSlot:', reservation.id, reservation.timeSlot);
        continue;
      }
      
      const timeSlotParts = reservation.timeSlot.split('-');
      if (timeSlotParts.length !== 2) {
        console.warn('Reservation has malformed timeSlot format:', reservation.id, reservation.timeSlot);
        continue;
      }
      
      const [existingStart, existingEnd] = timeSlotParts;
      
      // Validate that both start and end times exist and are valid
      if (!existingStart || !existingEnd) {
        console.warn('Reservation has missing start or end time:', reservation.id, { existingStart, existingEnd });
        continue;
      }
      
      const existingStartMinutes = timeToMinutes(existingStart);
      const existingEndMinutes = timeToMinutes(existingEnd);
      
      // Skip if time conversion failed (would return 0)
      if (existingStartMinutes === 0 && existingStart !== '00:00') {
        console.warn('Failed to convert start time:', reservation.id, existingStart);
        continue;
      }
      
      if (existingEndMinutes === 0 && existingEnd !== '00:00') {
        console.warn('Failed to convert end time:', reservation.id, existingEnd);
        continue;
      }
      
      // Check if the extension time overlaps with existing reservation
      if (currentEndMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes) {
        console.log('Conflict found with reservation:', reservation.id);
        return {
          available: false,
          conflictReason: `Conflict with reservation ${reservation.id} (${existingStart}-${existingEnd})`
        };
      }
    }
    
    // Check marina business hours (with seasonal support)
    const marinaSettings = await getMarinaSettings();
    if (marinaSettings) {
      const dayOfWeek = format(parseISO(date), 'EEEE').toLowerCase();
      const reservationDate = parseISO(date);
      
      // Check if the date is in season
      let isInSeason = false;
      if (marinaSettings.isSeasonalHoursEnabled && marinaSettings.seasonStartDate && marinaSettings.seasonEndDate) {
        // Parse the season start date
        const seasonStartDate = marinaSettings.seasonStartDate;
        let seasonStart;
        
        // Handle both date string formats: "MM-DD" and "YYYY-MM-DD"
        if (seasonStartDate.includes('-')) {
          if (seasonStartDate.split('-').length === 3) {
            // Full date with year
            seasonStart = parseISO(seasonStartDate);
          } else {
            // MM-DD format, add current year
            const currentYear = reservationDate.getFullYear();
            const [month, day] = seasonStartDate.split('-').map(Number);
            seasonStart = new Date(currentYear, month - 1, day);
          }
        }
        
        // Parse the season end date
        const seasonEndDate = marinaSettings.seasonEndDate;
        let seasonEnd;
        
        // Handle both date string formats: "MM-DD" and "YYYY-MM-DD"
        if (seasonEndDate.includes('-')) {
          if (seasonEndDate.split('-').length === 3) {
            // Full date with year
            seasonEnd = parseISO(seasonEndDate);
          } else {
            // MM-DD format, add current year
            const currentYear = reservationDate.getFullYear();
            const [month, day] = seasonEndDate.split('-').map(Number);
            seasonEnd = new Date(currentYear, month - 1, day);
          }
        }
        
        // Check if reservationDate is within season
        if (seasonStart && seasonEnd) {
          isInSeason = isWithinInterval(reservationDate, { start: seasonStart, end: seasonEnd });
          console.log(`Seasonal check for ${format(reservationDate, 'EEE MMM dd yyyy')}: season=${format(seasonStart, 'EEE MMM dd yyyy')} to ${format(seasonEnd, 'EEE MMM dd yyyy')}, isInSeason=${isInSeason}`);
        }
      }
      
      // Use the appropriate business hours based on season
      let businessHours;
      if (marinaSettings.isSeasonalHoursEnabled) {
        businessHours = isInSeason
          ? (marinaSettings.inSeasonHours || marinaSettings.businessHours)
          : (marinaSettings.outOfSeasonHours || marinaSettings.businessHours);
      } else {
        businessHours = marinaSettings.businessHours;
      }
      
      const dayHours = businessHours[dayOfWeek];
      
      if (dayHours && !dayHours.isClosed) {
        // Validate that we have a valid close time
        if (!dayHours.closeTime) {
          console.warn('Business hours missing closeTime for day:', dayOfWeek, dayHours);
        } else {
          const closeTime = timeToMinutes(dayHours.closeTime);
          if (closeTime > 0 && newEndMinutes > closeTime) {
            return {
              available: false,
              conflictReason: `Extension would exceed business hours (closes at ${dayHours.closeTime})`
            };
          }
        }
      } else {
        console.log(`Marina is closed on ${dayOfWeek}`);
        return {
          available: false,
          conflictReason: `Marina is closed on ${dayOfWeek}`
        };
      }
    }
    
    console.log('Extension available');
    return { available: true };
  } catch (error) {
    console.error('Error checking extension availability:', error);
    return {
      available: false,
      conflictReason: 'Error checking availability'
    };
  }
};

// Get available extension options for a reservation
export const getExtensionOptions = async (
  reservation: Reservation
): Promise<ExtensionOption[]> => {
  try {
    console.log('Getting extension options for reservation:', reservation.id);
    
    // Validate that reservation has required time data
    console.log('Reservation object:', {
      id: reservation.id,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      vesselType: reservation.vesselType,
      vesselId: reservation.vesselId,
      date: reservation.date
    });
    
    if (!reservation.endTime) {
      console.error('Reservation missing endTime:', reservation);
      return [];
    }
    
    if (!reservation.startTime) {
      console.error('Reservation missing startTime:', reservation);
      return [];
    }
    
    if (!reservation.vesselType) {
      console.error('Reservation missing vesselType:', reservation);
      return [];
    }
    
    const options: ExtensionOption[] = [];
    const maxExtensionHours = 8; // Maximum hours that can be added
    
    // Get current pricing for the vessel type
    const pricingOptions = await getVesselPricingOptions(reservation.vesselType);
    
    // Calculate base hourly rate
    let hourlyRate = 50; // Default fallback
    if (pricingOptions.durationOptions.length > 0) {
      // Use the first duration option to calculate hourly rate
      const firstOption = pricingOptions.durationOptions[0];
      hourlyRate = firstOption.price / firstOption.hours;
    }
    
    console.log('Using hourly rate:', hourlyRate);
    
    // Generate extension options (1, 2, 3, 4, 6, 8 hours)
    const extensionHours = [1, 2, 3, 4, 6, 8];
    
    for (const hours of extensionHours) {
      if (hours > maxExtensionHours) continue;
      
      const currentEndMinutes = timeToMinutes(convertTo24Hour(reservation.endTime));
      const newEndMinutes = currentEndMinutes + (hours * 60);
      const newEndTime = minutesToTime(newEndMinutes);
      
      // Check availability for this extension
      const availabilityCheck = await checkExtensionAvailability(
        reservation.vesselId,
        reservation.date,
        reservation.endTime,
        hours
      );
      
      const additionalCost = hours * hourlyRate;
      const currentTotal = reservation.finalPrice || reservation.totalPrice || 0;
      
      options.push({
        hours,
        endTime: reservation.endTime,
        newEndTime,
        additionalCost,
        totalNewCost: currentTotal + additionalCost,
        isAvailable: availabilityCheck.available,
        conflictReason: availabilityCheck.conflictReason
      });
    }
    
    console.log('Generated extension options:', options);
    return options;
  } catch (error) {
    console.error('Error getting extension options:', error);
    return [];
  }
};

// Extend a reservation
export const extendReservation = async (
  request: ExtensionRequest
): Promise<ExtensionResult> => {
  try {
    console.log('Extending reservation:', request);
    
    // Get the current reservation
    const reservationRef = doc(db, 'reservations', request.reservationId);
    
    return await runTransaction(db, async (transaction) => {
      const reservationDoc = await transaction.get(reservationRef);
      
      if (!reservationDoc.exists()) {
        throw new Error('Reservation not found');
      }
      
      const currentReservation = reservationDoc.data() as Reservation;
      
      // Validate that the reservation has the required time data
      if (!currentReservation.endTime) {
        throw new Error('Reservation missing end time');
      }
      
      if (!currentReservation.startTime) {
        throw new Error('Reservation missing start time');
      }
      
      // Check availability one more time within the transaction
      const availabilityCheck = await checkExtensionAvailability(
        currentReservation.vesselId,
        currentReservation.date,
        currentReservation.endTime,
        request.additionalHours
      );
      
      if (!availabilityCheck.available) {
        throw new Error(availabilityCheck.conflictReason || 'Extension not available');
      }
      
      // Calculate new end time
      const currentEndMinutes = timeToMinutes(convertTo24Hour(currentReservation.endTime));
      const newEndMinutes = currentEndMinutes + (request.additionalHours * 60);
      const newEndTime = minutesToTime(newEndMinutes);
      
      // Calculate additional cost
      const pricingOptions = await getVesselPricingOptions(currentReservation.vesselType);
      let hourlyRate = 50; // Default fallback
      if (pricingOptions.durationOptions.length > 0) {
        const firstOption = pricingOptions.durationOptions[0];
        hourlyRate = firstOption.price / firstOption.hours;
      }
      
      const additionalCost = request.additionalHours * hourlyRate;
      const currentTotal = currentReservation.finalPrice || currentReservation.totalPrice || 0;
      const newTotal = currentTotal + additionalCost;
      
             // Calculate new time slot
       const startTime24 = convertTo24Hour(currentReservation.startTime);
       const newEndTime24 = minutesToTime24(newEndMinutes);
       const newTimeSlot = `${startTime24}-${newEndTime24}` as TimeSlot;
      
      // Prepare the update data
      const updateData: Partial<Reservation> = {
        endTime: newEndTime,
        timeSlot: newTimeSlot,
        finalPrice: newTotal,
        totalPrice: newTotal, // Keep both for compatibility
        updatedAt: new Date(),
        version: (currentReservation.version || 0) + 1
      };
      
      // Update the reservation
      transaction.update(reservationRef, updateData);
      
             // Create/update availability block for the extended time
       const currentEndTime24 = convertTo24Hour(currentReservation.endTime);
       const extendedBlockId = `${currentReservation.vesselId}_${currentReservation.date}_${currentEndTime24}-${newEndTime24}`;
       const extendedBlockRef = doc(db, 'availability', extendedBlockId);
       
       transaction.set(extendedBlockRef, {
         id: extendedBlockId,
         vesselId: currentReservation.vesselId,
         date: currentReservation.date,
         timeSlot: `${currentEndTime24}-${newEndTime24}`,
         isAvailable: false,
         reservationId: request.reservationId,
         createdAt: Date.now(),
         updatedAt: Date.now(),
         isExtension: true
       });
      
      console.log('Reservation extended successfully');
      
      return {
        success: true,
        newEndTime,
        additionalCost,
        newTotalCost: newTotal,
        message: `Reservation extended by ${request.additionalHours} hour(s). New end time: ${newEndTime}`
      };
    });
  } catch (error) {
    console.error('Error extending reservation:', error);
    return {
      success: false,
      newEndTime: '',
      additionalCost: 0,
      newTotalCost: 0,
      message: 'Failed to extend reservation',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Complete extension process with history tracking
export const processReservationExtension = async (
  request: ExtensionRequest
): Promise<ExtensionResult> => {
  try {
    // Get the current reservation for history tracking
    const reservationRef = doc(db, 'reservations', request.reservationId);
    const reservationDoc = await getDoc(reservationRef);
    
    if (!reservationDoc.exists()) {
      throw new Error('Reservation not found');
    }
    
    const oldReservation = reservationDoc.data() as Reservation;
    
    // Perform the extension
    const result = await extendReservation(request);
    
    if (result.success) {
      // Track the extension in history
      try {
        await trackReservationUpdate(
          request.reservationId,
          oldReservation,
          {
            ...oldReservation,
            endTime: result.newEndTime,
            finalPrice: result.newTotalCost,
            totalPrice: result.newTotalCost
          },
          request.extendedBy,
          request.channel,
          `Extended reservation by ${request.additionalHours} hour(s). Additional cost: $${result.additionalCost.toFixed(2)}. ${request.notes || ''}`
          // Note: Extension service doesn't have access to userProfile, so staff name won't show for extensions
        );
      } catch (historyError) {
        console.error('Error tracking extension in history:', historyError);
        // Don't fail the extension if history tracking fails
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error processing reservation extension:', error);
    return {
      success: false,
      newEndTime: '',
      additionalCost: 0,
      newTotalCost: 0,
      message: 'Failed to process extension',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Get extension history for a reservation
export const getReservationExtensions = async (reservationId: string): Promise<any[]> => {
  try {
    // This could be expanded to track detailed extension history
    // For now, it would be captured in the main reservation history
    return [];
  } catch (error) {
    console.error('Error getting reservation extensions:', error);
    return [];
  }
}; 